import { useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";
import { CONVERT } from "@/utils/ballisticsMath";
import { formatHoldover, HOLDOVER_UNIT_LABEL } from "@/utils/units";
import { useT } from "@/i18n/translations";

const buildTooltip = (t, holdoverUnit, imperial, clickMrad) => ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  const clicks = clickMrad ? Math.round(Math.abs(d.dropMrad) / clickMrad) : 0;
  const dir = d.dropMrad < 0 ? t("dir.up") : t("dir.down");
  return (
    <div className="rounded-md border border-dase-border bg-dase-panel2 p-3 font-mono text-[11px] shadow-lg">
      <div className="mb-1 text-xs font-bold text-dase-red">
        {d.dist.toFixed(0)} {imperial ? "yd" : "m"}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-zinc-300">
        <span className="text-zinc-500">{t("chart.tt.drop")}</span>
        <span>{d.drop.toFixed(2)} {HOLDOVER_UNIT_LABEL[holdoverUnit]}</span>
        <span className="text-zinc-500">{t("chart.tt.correction")}</span>
        <span className="text-dase-red">{clicks} {t("reticle.clicks")} {dir}</span>
        <span className="text-zinc-500">{t("chart.tt.velocity")}</span>
        <span>{d.vel.toFixed(1)} {imperial ? "fps" : "m/s"}</span>
        <span className="text-zinc-500">{t("chart.tt.energy")}</span>
        <span>{d.energy.toFixed(1)} {imperial ? "ft-lb" : "J"}</span>
        <span className="text-zinc-500">{t("chart.tt.time")}</span>
        <span>{(d.timeS * 1000).toFixed(0)} ms</span>
      </div>
    </div>
  );
};

export const TrajectoryChart = ({
  samples,
  holdoverUnit,
  unitSystem,
  targetDistanceM,
  zeroDistanceM,
  clickMrad,
}) => {
  const t = useT();
  const [overlay, setOverlay] = useState("velocity");
  const imperial = unitSystem === "imperial";

  const overlays = [
    { id: "none", label: t("chart.overlayNone") },
    { id: "velocity", label: t("chart.velocity") },
    { id: "energy", label: t("chart.energy") },
    { id: "drift", label: t("chart.wind") },
  ];

  const overlayColor =
    overlay === "velocity" ? "#3b82f6" : overlay === "energy" ? "#eab308" : "#14b8a6";

  const data = samples.map((sm) => ({
    dist: imperial ? CONVERT.mToYd(sm.distanceM) : sm.distanceM,
    drop: formatHoldover(sm.dropM, sm.dropMrad, sm.dropMoa, holdoverUnit),
    driftVal: formatHoldover(sm.driftM, sm.driftMrad, sm.driftMoa, holdoverUnit),
    dropMrad: sm.dropMrad,
    vel: imperial ? CONVERT.msToFps(sm.velocityMs) : sm.velocityMs,
    energy: imperial ? CONVERT.jToFtlb(sm.energyJ) : sm.energyJ,
    timeS: sm.timeS,
  }));

  const targetDisp = imperial ? CONVERT.mToYd(targetDistanceM) : targetDistanceM;
  const zeroDisp = imperial ? CONVERT.mToYd(zeroDistanceM) : zeroDistanceM;

  return (
    <div className="rounded-md border border-dase-border bg-dase-panel p-4" data-testid="trajectory-chart">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-200">
          {t("chart.title")} ({HOLDOVER_UNIT_LABEL[holdoverUnit]})
        </h2>
        <div className="flex items-center gap-1">
          <span className="mr-1 text-[10px] uppercase tracking-wider text-zinc-500">{t("chart.overlayLabel")}</span>
          {overlays.map((o) => (
            <button
              key={o.id}
              data-testid={`chart-overlay-${o.id}`}
              onClick={() => setOverlay(o.id)}
              className={`rounded px-2 py-1 text-[10px] font-bold uppercase transition-colors ${
                overlay === o.id ? "bg-dase-red text-white" : "border border-dase-border text-zinc-400 hover:text-zinc-200"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-[340px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 20, bottom: 20, left: 0 }}>
            <CartesianGrid stroke="#262626" strokeDasharray="3 3" />
            <XAxis
              dataKey="dist"
              type="number"
              domain={[0, "dataMax"]}
              stroke="#666"
              tick={{ fill: "#a3a3a3", fontSize: 11, fontFamily: "monospace" }}
              label={{
                value: `${t("chart.distance")} (${imperial ? "yd" : "m"})`,
                position: "insideBottom",
                offset: -10,
                fill: "#666",
                fontSize: 11,
              }}
            />
            <YAxis
              yAxisId="drop"
              stroke="#666"
              tick={{ fill: "#a3a3a3", fontSize: 11, fontFamily: "monospace" }}
              width={50}
            />
            {overlay !== "none" && (
              <YAxis
                yAxisId="overlay"
                orientation="right"
                stroke={overlayColor}
                tick={{ fill: overlayColor, fontSize: 10, fontFamily: "monospace" }}
                width={45}
              />
            )}
            <Tooltip content={buildTooltip(t, holdoverUnit, imperial, clickMrad)} />
            <ReferenceLine yAxisId="drop" y={0} stroke="#525252" strokeWidth={1.5} />
            <ReferenceLine
              yAxisId="drop"
              x={zeroDisp}
              stroke="#3f3f46"
              strokeDasharray="4 4"
              label={{ value: "ZERO", fill: "#71717a", fontSize: 9, position: "top" }}
            />
            <ReferenceLine
              yAxisId="drop"
              x={targetDisp}
              stroke="#cc0000"
              strokeWidth={1.5}
              strokeDasharray="5 3"
              label={{ value: imperial ? "TARGET" : "KOHDE", fill: "#cc0000", fontSize: 10, position: "top" }}
            />
            <Line
              yAxisId="drop"
              type="monotone"
              dataKey="drop"
              stroke="#cc0000"
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 4, fill: "#cc0000" }}
              isAnimationActive={false}
            />
            {overlay === "velocity" && (
              <Line yAxisId="overlay" type="monotone" dataKey="vel" stroke="#3b82f6" strokeWidth={1.5} dot={false} isAnimationActive={false} />
            )}
            {overlay === "energy" && (
              <Line yAxisId="overlay" type="monotone" dataKey="energy" stroke="#eab308" strokeWidth={1.5} dot={false} isAnimationActive={false} />
            )}
            {overlay === "drift" && (
              <Line yAxisId="overlay" type="monotone" dataKey="driftVal" stroke="#14b8a6" strokeWidth={1.5} strokeDasharray="5 3" dot={false} isAnimationActive={false} />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TrajectoryChart;
